import React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import type { i18n } from 'i18next';
import { bindActionCreators, Dispatch } from 'redux';
import { IAppState } from '../redux/store/types';
import { ILatestPublicRecord, ILatestPublicRecordSummary, ViewMode } from '../redux/records/types';
import { openCenteredPopup80, timeShortDisplay } from '../utils/utils';
import { ArrowRight, FileText, Link } from 'lucide-react';
import {
  addRecordAction,
  setCurrentRecordAction,
  prepareLinkRecordCommandAction,
  prepareReadRecordsCommandAction,
  resetRecordsAction,
  setShowAllLinkableAction,
  setSelectedSummariesRecordIdAction,
  setRecordAsNonLinkableAction,
  setViewModeAction,
} from '../redux/records/actions';
import { handlePdfDownload } from 'services/rest';
import { toast } from 'react-toastify';

class LatestNPublicRecords extends React.Component<ILatestNPublicRecordsProps> {
  render() {
    const { latestPublicRecords, t } = this.props;

    return (
      /*latestPublicRecords.length === 0 ? (
      <div className='latest-n-public-records'>
        <div className='latest-n-public-records-header'>
          <h3>{t('records.latestPublicRecords')}</h3>
        </div>
        <div className='latest-n-public-records-empty'>{t('records.noPublicRecords')}</div>
      </div>
    ) :*/ latestPublicRecords.length > 0 && (
        <div className='latest-n-public-records-container'>
          <div className='latest-n-public-records'>
            <div className='latest-n-public-records-header'>
              <h3>{t('records.latestPublicRecords')}</h3>
            </div>
            <div className='latest-n-public-records-grid'>
              <div className='latest-n-public-records-grid-header'>
                <div className='grid-header-cell'>{t('common.fields.updatedAt')}</div>
                <div className='grid-header-cell'>{t('common.fields.subject')}</div>
                <div className='grid-header-cell'>{t('common.by')}</div>
                <div className='grid-header-cell' title={t('records.linkWithDescription')}>
                  {t('records.link')}
                </div>
              </div>
              {latestPublicRecords.map((record) => (
                <div
                  key={record.record_id}
                  className={`latest-n-public-records-grid-row table-row record ${(record.isLinkable && 'linkable') || ''} ${
                    (record.record_id === this.props.selectedSummariesRecordId && 'current') || ''
                  } ${(new Date().getTime() - new Date(record.updated_at).getTime() < 5 * 60 * 1000 && 'updated-recently') || ''}`}
                  onClick={() => this.handleSummariesClick(record)}
                >
                  <div className='grid-cell grid-cell-date'>{timeShortDisplay(new Date(record.updated_at))}</div>
                  <div className='grid-cell grid-cell-subject' title={`${record.summaries?.length} ${t('summaries.title')}`}>
                    {record.subject}
                  </div>
                  <div className='grid-cell grid-cell-saas-tenant'>{record.saas_tenant_name}</div>
                  <div className='grid-cell actions'>
                    {record.isLinkable ? (
                      <button onClick={(event) => this.handleLinkRecord(record, event)} className='action-button link' title={t('records.linkWithDescription')}>
                        {<Link />}
                      </button>
                    ) : (
                      <button onClick={(event) => this.handleGoToRecord(record, event)} className='action-button next' title={t('common.next')}>
                        {<ArrowRight />}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {this.renderSummariesTable()}
        </div>
      )
    );
  }

  /**
   * Render the summaries table for the selected record.
   */
  renderSummariesTable(): React.ReactNode {
    const { selectedSummariesRecordId, latestPublicRecords, t } = this.props;

    if (!selectedSummariesRecordId) {
      return null;
    }

    const selectedRecord = latestPublicRecords.find((record) => record.record_id === selectedSummariesRecordId);
    if (!selectedRecord || !selectedRecord.summaries?.length) {
      return null;
    }

    return (
      <div className='summaries-table-container'>
        <div className='summaries-table-header'>
          <h4>{selectedRecord.subject}</h4>
        </div>
        <div className='summaries-table-grid'>
          <div className='summaries-table-grid-header'>
            <div className='grid-header-cell'>{t('common.fields.createdAt')}</div>
            <div className='grid-header-cell'>{t('common.fields.subject')}</div>
            <div className='grid-header-cell'>{t('common.by')}</div>
            <div className='grid-header-cell' />
          </div>
          {selectedRecord.summaries.map((summary) => (
            <div key={summary.id} className='summaries-table-grid-row table-row'>
              <div className='grid-cell grid-cell-created-at'>{timeShortDisplay(new Date(summary.created_at))}</div>
              <div className='grid-cell grid-cell-created-subject'>{summary.subject}</div>
              <div className='grid-cell grid-cell-created-by'>{summary.saas_tenant_name}</div>
              <div className='grid-cell'>
                <button className='action-button pdf' title={t('summaries.downloadPdf')} onClick={() => this.handlePdfDownload(summary)} autoFocus>
                  <FileText />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  /**
   * Handle clicking on summaries to show/hide summaries table.
   */
  handleSummariesClick(record: ILatestPublicRecord): void {
    const { selectedSummariesRecordId } = this.props;
    if (record.summaries?.length > 0) this.props.setSelectedSummariesRecordIdAction(record.record_id !== selectedSummariesRecordId ? record.record_id : null);
  }

  /**
   * Handle linking a public record to the current user.
   */
  handleLinkRecord(latestRecord: ILatestPublicRecord, event: React.MouseEvent): void {
    event.stopPropagation();
    const { record_id, subject, saas_tenant_id, saas_tenant_name } = latestRecord;
    this.props.prepareLinkRecordCommandAction(record_id, saas_tenant_id);
    if (this.props.showAllLinkable) {
      this.props.resetRecordsAction();
      setTimeout(() => this.props.prepareReadRecordsCommandAction({ readAllLinkable: false, searchText: this.props.searchText }), 500);
      this.props.setShowAllLinkableAction(false);
    }
    this.props.addRecordAction({ record_id, user_text: subject, ai_text: subject, llm_profile_id: '', saas_tenant_name, onroute: true }); // ai_text remains '' until the backend confirms the insertion, to signal 'work in progress'...
    this.props.setViewModeAction(ViewMode.SHOW_CONDENSED_RECORDS_VIEW);
    this.props.setRecordAsNonLinkableAction(record_id); // Mark this latest public record as non-linkable immediately in the UI
  }

  /**
   * Handle navigating to a chat in the private and linked chats.
   */
  handleGoToRecord(latestRecord: ILatestPublicRecord, event: React.MouseEvent): void {
    event.stopPropagation();
    const { record_id } = latestRecord;
    if (!this.props.showAllLinkable) {
      // read and position to the complete selected record.
      this.props.prepareReadRecordsCommandAction({ record_id });
      this.props.setCurrentRecordAction(record_id);
    } else {
      this.props.resetRecordsAction();
      setTimeout(() => {
        // switch to private-and-linked view, and position to the complete selected record.
        this.props.prepareReadRecordsCommandAction({ readAllLinkable: false, searchText: this.props.searchText });
        setTimeout(() => this.props.setCurrentRecordAction(record_id), 3000);
      }, 500);
      this.props.setShowAllLinkableAction(false);
    }
    this.props.setViewModeAction(ViewMode.SHOW_CONDENSED_RECORDS_VIEW);
  }

  /**
   * Handles the PDF download process for a summary, including waiting for recent updates
   * to be processed by the backend stream processor if necessary.
   * @param {ISummary} summary - The summary to download as PDF
   * @returns {Promise<void>}
   * @private
   */
  private async handlePdfDownload(summary: ILatestPublicRecordSummary): Promise<void> {
    const { t } = this.props;

    // Download the PDF
    const pdfUrl: string | null = await handlePdfDownload(summary.id, this.props.JWT as string);
    if (!pdfUrl) toast.error(t('messages.error'));
    else openCenteredPopup80(pdfUrl);
  }
}

interface ILatestNPublicRecordsProps {
  latestPublicRecords: ILatestPublicRecord[];
  selectedSummariesRecordId: string | null;
  t: (key: string) => string;
  i18n: i18n;
  searchText: string;
  addRecordAction: typeof addRecordAction;
  setCurrentRecordAction: typeof setCurrentRecordAction;
  prepareLinkRecordCommandAction: typeof prepareLinkRecordCommandAction;
  prepareReadRecordsCommandAction: typeof prepareReadRecordsCommandAction;
  resetRecordsAction: typeof resetRecordsAction;
  showAllLinkable: boolean;
  setShowAllLinkableAction: typeof setShowAllLinkableAction;
  setSelectedSummariesRecordIdAction: typeof setSelectedSummariesRecordIdAction;
  setRecordAsNonLinkableAction: typeof setRecordAsNonLinkableAction;
  JWT: string;
  userId: string;
  setViewModeAction: typeof setViewModeAction;
}

const mapStateToProps = (state: IAppState) => ({
  latestPublicRecords: state.records.latestPublicRecords,
  selectedSummariesRecordId: state.records.selectedSummariesRecordId,
  searchText: state.records.searchText,
  showAllLinkable: state.records.showAllLinkable,
  JWT: state.auth.JWT as string,
  userId: state.auth.userId as string,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      addRecordAction,
      setCurrentRecordAction,
      prepareLinkRecordCommandAction,
      prepareReadRecordsCommandAction,
      resetRecordsAction,
      setShowAllLinkableAction,
      setSelectedSummariesRecordIdAction,
      setRecordAsNonLinkableAction,
      setViewModeAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(LatestNPublicRecords));
